/**
 * Auth Modal Component
 *
 * Modern, harmonized login/register modal for Supabase authentication.
 * Opens directly from TopBar user icon click.
 * Mobile-responsive design.
 * Uses React Portal to render outside the DOM hierarchy.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useSupabaseAuth } from '../../hooks/useSupabaseAuth';
import { Icons } from '../Icons';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';
import { StorageService } from '../../services/storage';
import { syncSecureStorage } from '../../services/secureTokenStorage';
import { mapAuthError } from '../../services/errorMap';

// Social provider icons
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const DiscordIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

// Keys for remember me storage (using simple localStorage via StorageService)
const REMEMBER_EMAIL_KEY = 'remember-me-email';
const REMEMBER_ME_KEY = 'remember-me-enabled';

// Rate limiting constants — sourced from the central config module so
// AuthModal, EmailVerificationPage and pinService stay in sync. Tweak
// LOGIN_MAX_ATTEMPTS / LOGIN_LOCKOUT_MS in src/config/authTimeouts.ts.
import { LOGIN_MAX_ATTEMPTS, LOGIN_LOCKOUT_MS } from '../../config/authTimeouts';
const LOCKOUT_STORAGE_KEY = 'auth-rate-limit';

interface RateLimitState {
  attempts: number;
  lockoutUntil: number | null;
}

// Get rate limit state from storage
const getRateLimitState = (): RateLimitState => {
  try {
    const stored = syncSecureStorage.getItem(LOCKOUT_STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored);
      // Check if lockout has expired
      if (state.lockoutUntil && Date.now() > state.lockoutUntil) {
        // Reset after lockout expires
        return { attempts: 0, lockoutUntil: null };
      }
      return state;
    }
  } catch {
    // Invalid data
  }
  return { attempts: 0, lockoutUntil: null };
};

// Save rate limit state to storage
const saveRateLimitState = (state: RateLimitState): void => {
  syncSecureStorage.setItem(LOCKOUT_STORAGE_KEY, JSON.stringify(state));
};

// Reset rate limit (on successful login)
const resetRateLimit = (): void => {
  syncSecureStorage.removeItem(LOCKOUT_STORAGE_KEY);
};

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const { t } = useTranslation();
  const { login, register, isLoading, error: authError, clearError } = useAuthStore();
  const { signInWithGoogle, loading: oauthLoading } = useSupabaseAuth();

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordScore, setPasswordScore] = useState(0);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Rate limiting state
  const [rateLimitState, setRateLimitState] = useState<RateLimitState>(() => getRateLimitState());
  const [lockoutRemaining, setLockoutRemaining] = useState<number>(0);

  // Update lockout countdown timer
  useEffect(() => {
    if (rateLimitState.lockoutUntil && rateLimitState.lockoutUntil > Date.now()) {
      const interval = setInterval(() => {
        const remaining = Math.max(0, rateLimitState.lockoutUntil! - Date.now());
        setLockoutRemaining(remaining);

        if (remaining === 0) {
          // Lockout expired, reset state
          setRateLimitState({ attempts: 0, lockoutUntil: null });
          saveRateLimitState({ attempts: 0, lockoutUntil: null });
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [rateLimitState.lockoutUntil]);

  // Check for active lockout on mount
  useEffect(() => {
    const state = getRateLimitState();
    setRateLimitState(state);
    if (state.lockoutUntil && state.lockoutUntil > Date.now()) {
      setLockoutRemaining(state.lockoutUntil - Date.now());
    }
  }, [isOpen]);

  // Handle password strength changes
  const handlePasswordStrengthChange = useCallback((score: number, isValid: boolean) => {
    setPasswordScore(score);
    setIsPasswordValid(isValid);
  }, []);

  // Reset form when modal opens/closes & load remembered email
  useEffect(() => {
    if (isOpen) {
      setMode('login');
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setLocalError(null);
      clearError();

      // Load remembered email from localStorage
      const savedEmail = StorageService.get<string>(REMEMBER_EMAIL_KEY, '');
      const savedRememberMe = StorageService.get<boolean>(REMEMBER_ME_KEY, false);
      if (savedEmail && savedRememberMe) {
        setEmail(savedEmail);
        setRememberMe(true);
      } else {
        setEmail('');
        setRememberMe(false);
      }
    }
  }, [isOpen, clearError]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Create portal target
  const portalTarget = document.body;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    // Check if user is locked out
    const currentState = getRateLimitState();
    if (currentState.lockoutUntil && currentState.lockoutUntil > Date.now()) {
      const remainingSeconds = Math.ceil((currentState.lockoutUntil - Date.now()) / 1000);
      setLocalError(
        t('auth.tooManyAttempts', 'Trop de tentatives. Réessayez dans {{seconds}} secondes.', {
          seconds: remainingSeconds,
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      await login(email, password);

      // Reset rate limit on successful login
      resetRateLimit();
      setRateLimitState({ attempts: 0, lockoutUntil: null });

      // Save or clear remembered email based on checkbox
      if (rememberMe) {
        StorageService.set(REMEMBER_EMAIL_KEY, email);
        StorageService.set(REMEMBER_ME_KEY, true);
      } else {
        StorageService.remove(REMEMBER_EMAIL_KEY);
        StorageService.remove(REMEMBER_ME_KEY);
      }

      onClose();
    } catch (err: any) {
      // Increment failed attempts
      const newAttempts = currentState.attempts + 1;
      let newLockoutUntil: number | null = null;

      // Translate the underlying Supabase error into a user-facing message
      // with a recovery hint. Falls back to the localized default if the
      // i18n key is missing — never exposes raw Supabase strings.
      const mapped = mapAuthError(err);
      const friendly = t(mapped.messageKey, mapped.messageDefault);

      if (newAttempts >= LOGIN_MAX_ATTEMPTS) {
        // Apply lockout
        newLockoutUntil = Date.now() + LOGIN_LOCKOUT_MS;
        setLocalError(
          t('auth.accountLocked', 'Compte temporairement bloqué. Réessayez dans 1 minute.')
        );
      } else {
        const remainingAttempts = LOGIN_MAX_ATTEMPTS - newAttempts;
        setLocalError(
          t('auth.loginErrorWithAttempts', '{{error}}. Il vous reste {{remaining}} tentative(s).', {
            error: friendly,
            remaining: remainingAttempts,
          })
        );
      }

      const newState = { attempts: newAttempts, lockoutUntil: newLockoutUntil };
      saveRateLimitState(newState);
      setRateLimitState(newState);

      if (newLockoutUntil) {
        setLockoutRemaining(LOGIN_LOCKOUT_MS);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (password !== confirmPassword) {
      setLocalError(t('auth.passwordMismatch', 'Les mots de passe ne correspondent pas'));
      return;
    }

    if (password.length < 8) {
      setLocalError(t('auth.passwordTooShort', 'Le mot de passe doit contenir au moins 8 caractères'));
      return;
    }

    // Check password strength with zxcvbn
    if (!isPasswordValid) {
      setLocalError(t('auth.passwordTooWeak', 'Le mot de passe n\'est pas assez fort. Utilisez un mélange de lettres, chiffres et symboles.'));
      return;
    }

    setIsSubmitting(true);

    try {
      await register(email, password, username || undefined);
      onClose();
    } catch (err: any) {
      // Same translation pipeline as login: convert Supabase errors to a
      // localized, actionable message (e.g. "Un compte existe déjà avec
      // cet email" → CTA to sign in).
      const mapped = mapAuthError(err);
      setLocalError(t(mapped.messageKey, mapped.messageDefault));
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setLocalError(null);
    clearError();
    setPassword('');
    setConfirmPassword('');
  };

  const displayError = localError || authError;

  // Common input styles
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    transition: 'all 0.2s',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text)',
    marginBottom: '8px',
  };

  return createPortal(
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '16px',
        paddingTop: '40px',
        overflowY: 'auto',
        animation: 'authFadeIn 0.2s ease-out',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '380px',
          background: 'linear-gradient(145deg, rgba(25, 25, 35, 0.98), rgba(15, 15, 25, 0.99))',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
          position: 'relative',
          zIndex: 100000,
          animation: 'authSlideUp 0.3s ease-out',
          marginBottom: '40px',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: 'none',
            borderRadius: '8px',
            color: 'var(--muted)',
            cursor: 'pointer',
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            zIndex: 10,
          }}
        >
          <Icons.X size={18} />
        </button>

        {/* Header - Compact */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
            }}
          >
            <Icons.User size={24} style={{ color: 'white' }} />
          </div>

          <h2
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '4px',
            }}
          >
            {mode === 'login'
              ? t('auth.welcomeBack', 'Bon retour!')
              : t('auth.createAccount', 'Créer un compte')}
          </h2>
          <p
            style={{
              fontSize: '13px',
              color: 'var(--muted)',
            }}
          >
            {mode === 'login'
              ? t('auth.loginSubtitle', 'Synchronisez vos données')
              : t('auth.registerSubtitle', 'Rejoignez Edouard.ai')}
          </p>
        </div>

        {/* Error Message */}
        {displayError && (
          <div
            style={{
              padding: '10px 12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '10px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#f87171',
              fontSize: '13px',
            }}
          >
            <Icons.AlertCircle size={16} />
            <span style={{ flex: 1 }}>{displayError}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
          {/* Username Field (Register only) */}
          {mode === 'register' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>
                <Icons.User size={14} style={{ color: 'var(--muted)' }} />
                <span>
                  {t('auth.username', 'Pseudo')}{' '}
                  <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                    ({t('auth.optional', 'optionnel')})
                  </span>
                </span>
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('auth.usernamePlaceholder', 'Votre pseudo')}
                style={inputStyle}
              />
            </div>
          )}

          {/* Email Field */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>
              <Icons.Mail size={14} style={{ color: 'var(--muted)' }} />
              {t('auth.email', 'Email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder', 'votre@email.com')}
              required
              autoComplete="email"
              autoFocus
              style={inputStyle}
            />
          </div>

          {/* Password Field */}
          <div style={{ marginBottom: mode === 'register' ? '12px' : '12px' }}>
            <label style={labelStyle}>
              <Icons.Lock size={14} style={{ color: 'var(--muted)' }} />
              {t('auth.password', 'Mot de passe')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={
                  mode === 'register'
                    ? t('auth.passwordMinLength', 'Min. 8 caractères')
                    : '••••••••'
                }
                required
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                minLength={mode === 'register' ? 8 : undefined}
                style={{ ...inputStyle, paddingRight: '44px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  padding: '4px',
                  display: 'flex',
                }}
              >
                {showPassword ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
              </button>
            </div>
            {/* Password Strength Meter (Register only) */}
            {mode === 'register' && (
              <PasswordStrengthMeter
                password={password}
                minScore={3}
                onStrengthChange={handlePasswordStrengthChange}
              />
            )}
          </div>

          {/* Remember Me Checkbox (Login only) */}
          {mode === 'login' && (
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'var(--text)',
                }}
              >
                <div
                  onClick={() => setRememberMe(!rememberMe)}
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '5px',
                    border: rememberMe
                      ? '2px solid #6366f1'
                      : '2px solid rgba(255, 255, 255, 0.2)',
                    background: rememberMe
                      ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                      : 'rgba(255, 255, 255, 0.03)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  {rememberMe && <Icons.Check size={12} style={{ color: 'white' }} />}
                </div>
                <span onClick={() => setRememberMe(!rememberMe)}>
                  {t('auth.rememberMe', 'Se souvenir de moi')}
                </span>
              </label>
            </div>
          )}

          {/* Confirm Password Field (Register only) */}
          {mode === 'register' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                <Icons.Lock size={14} style={{ color: 'var(--muted)' }} />
                {t('auth.confirmPassword', 'Confirmer')}
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.confirmPasswordPlaceholder', 'Confirmer le mot de passe')}
                required
                autoComplete="new-password"
                style={inputStyle}
              />
              {password && confirmPassword && password !== confirmPassword && (
                <p
                  style={{
                    fontSize: '11px',
                    color: '#f87171',
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Icons.AlertCircle size={12} />
                  {t('auth.passwordMismatch', 'Les mots de passe ne correspondent pas')}
                </p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            style={{
              width: '100%',
              padding: '12px',
              background:
                isSubmitting || isLoading
                  ? 'rgba(99, 102, 241, 0.5)'
                  : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: isSubmitting || isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow:
                isSubmitting || isLoading ? 'none' : '0 4px 15px rgba(99, 102, 241, 0.3)',
            }}
          >
            {isSubmitting || isLoading ? (
              <>
                <Icons.Loader size={16} style={{ animation: 'authSpin 1s linear infinite' }} />
                {mode === 'login'
                  ? t('auth.loggingIn', 'Connexion...')
                  : t('auth.registering', 'Inscription...')}
              </>
            ) : (
              <>
                {mode === 'login' ? <Icons.LogIn size={16} /> : <Icons.UserPlus size={16} />}
                {mode === 'login'
                  ? t('auth.login', 'Se connecter')
                  : t('auth.register', 'Créer mon compte')}
              </>
            )}
          </button>
        </form>

        {/* Social Login Divider */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            margin: '20px 0 16px',
          }}
        >
          <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.1)' }} />
          <span style={{ color: 'var(--muted)', fontSize: '11px', textTransform: 'uppercase' }}>
            {t('auth.orContinueWith', 'ou continuer avec')}
          </span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.1)' }} />
        </div>

        {/* Social Login Buttons - Disabled for now (not configured) */}
        <div style={{ display: 'flex', gap: '10px', opacity: 0.5 }}>
          <button
            type="button"
            disabled={true}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              background: 'rgba(255, 255, 255, 0.02)',
              color: 'var(--muted)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
            title={t('auth.comingSoon', 'Bientôt disponible')}
          >
            <GoogleIcon />
          </button>

          <button
            type="button"
            disabled={true}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              background: 'rgba(255, 255, 255, 0.02)',
              color: 'var(--muted)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
            title={t('auth.comingSoon', 'Bientôt disponible')}
          >
            <XIcon />
          </button>

          <button
            type="button"
            disabled={true}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              background: 'rgba(255, 255, 255, 0.02)',
              color: 'var(--muted)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
            title={t('auth.comingSoon', 'Bientôt disponible')}
          >
            <DiscordIcon />
          </button>
        </div>
        {/* Coming soon message */}
        <p style={{
          textAlign: 'center',
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          {t('auth.socialLoginComingSoon', 'Connexion sociale bientôt disponible')}
        </p>

        {/* Switch Mode */}
        <div
          style={{
            marginTop: '16px',
            textAlign: 'center',
            fontSize: '13px',
            color: 'var(--muted)',
          }}
        >
          {mode === 'login'
            ? t('auth.noAccount', 'Pas encore de compte ?')
            : t('auth.hasAccount', 'Déjà un compte ?')}{' '}
          <button
            onClick={switchMode}
            style={{
              background: 'none',
              border: 'none',
              color: '#818cf8',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            {mode === 'login'
              ? t('auth.createAccountBtn', 'Créer un compte')
              : t('auth.loginBtn', 'Se connecter')}
          </button>
        </div>

        {/* Info Box */}
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(99, 102, 241, 0.08)',
            border: '1px solid rgba(99, 102, 241, 0.15)',
            borderRadius: '10px',
            fontSize: '11px',
            color: 'var(--muted)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '4px',
              color: '#818cf8',
              fontWeight: 500,
              fontSize: '12px',
            }}
          >
            <Icons.Shield size={12} />
            {t('auth.secureData', 'Vos données sont sécurisées')}
          </div>
          <p style={{ lineHeight: 1.4, margin: 0 }}>
            {t(
              'auth.secureDataDesc',
              'Synchronisation cloud optionnelle. Vos données locales restent toujours prioritaires.'
            )}
          </p>
        </div>
      </div>

      <style>
        {`
          @keyframes authFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes authSlideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes authSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>,
    portalTarget
  );
}
